/**
 * Custom hook for moderation API operations
 * Centralizes all API calls with proper authentication
 */

import { useController } from "@rest-hooks/react";
import { Auth } from "aws-amplify";
import { ModerationResource, ModerationConfig, ModerationConfigResponse, BlocklistItem, ModerationAction } from "../../../api/moderation";
import { BASE_URL } from "../../../api/constants";

/**
 * Helper to make authenticated fetch requests with JSON body
 */
const authFetch = async (path: string, method: string, body: unknown) => {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken();
  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || "Request failed");
  }

  return response.json();
};

export const useModerationApi = () => {
  const { fetch } = useController();

  // Moderation Flags
  const refreshFlags = (params?: { meetingId?: string; action?: ModerationAction; limit?: number; nextToken?: string }) => {
    if (params) {
      return fetch(ModerationResource.getFlags, params);
    }
    return fetch(ModerationResource.getFlags, {});
  };

  // Configuration
  const refreshConfig = () => {
    return fetch(ModerationResource.getConfig);
  };

  const getConfig = async (): Promise<ModerationConfigResponse> => {
    return fetch(ModerationResource.getConfig);
  };

  const updateConfig = async (config: ModerationConfig) => {
    return fetch(ModerationResource.updateConfig, config);
  };

  // Blocklist
  const refreshBlocklist = () => {
    return fetch(ModerationResource.getBlocklist);
  };

  const addBlocklistItem = async (item: BlocklistItem) => {
    return authFetch("/admin/chat/moderation/blocklist", "POST", item);
  };

  const deleteBlocklistItem = async (item: BlocklistItem) => {
    return authFetch("/admin/chat/moderation/blocklist", "DELETE", item);
  };

  // Allowlist
  const refreshAllowlist = () => {
    return fetch(ModerationResource.getAllowlist);
  };

  const addAllowlistWord = async (value: string) => {
    return authFetch("/admin/chat/moderation/allowlist", "POST", { value });
  };

  const deleteAllowlistWord = async (value: string) => {
    return authFetch("/admin/chat/moderation/allowlist", "DELETE", { value });
  };

  return {
    // Flags
    refreshFlags,

    // Config
    refreshConfig,
    getConfig,
    updateConfig,

    // Blocklist
    refreshBlocklist,
    addBlocklistItem,
    deleteBlocklistItem,

    // Allowlist
    refreshAllowlist,
    addAllowlistWord,
    deleteAllowlistWord,
  };
};
